import { AlertTriangle, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { resetEmptyAnalyses } from "@/lib/edgeFunctions";
import { useToast } from "@/hooks/use-toast";

interface AnalysisErrorProps {
  message?: string | null;
  fixtureId?: number;
  onRegenerate?: () => void;
}

export function AnalysisError({ message, fixtureId, onRegenerate }: AnalysisErrorProps) {
  const { t } = useTranslation(['analysis', 'errors']);
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [isRegenerating, setIsRegenerating] = useState(false);

  const handleRegenerate = async () => {
    if (!fixtureId) return;
    
    setIsRegenerating(true);
    try {
      const result = await resetEmptyAnalyses([fixtureId]);
      if (result?.reset_count > 0) {
        toast({
          title: t('errors:analysisQueued'),
          description: t('errors:analysisQueuedDescription'),
        });
      } else {
        toast({
          title: t('errors:noRecords'),
          description: t('errors:noAnalysisFound'),
          variant: "destructive",
        });
      }
      onRegenerate?.();
    } catch (error: any) {
      toast({
        title: t('errors:regenerateError'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <Card className="bg-destructive/5 border-destructive/20 sketch-border">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-destructive/10 p-4 mb-4">
          <AlertTriangle className="h-8 w-8 text-destructive" />
        </div>
        <h3 className="text-lg font-bold mb-2">
          {t('analysis:status.unavailable')}
        </h3>
        <p className="text-muted-foreground text-sm max-w-sm mb-4">
          {message || t('analysis:messages.unableToGenerate')}
        </p>
        
        {/* Admin regenerate button */}
        {isAdmin && fixtureId && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleRegenerate}
            disabled={isRegenerating}
            className="mt-2"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRegenerating ? 'animate-spin' : ''}`} />
            {isRegenerating ? t('analysis:regenerate.regenerating') : t('analysis:regenerate.regenerateAnalysis')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
